import React, { useState } from "react";
import {
  Box, Typography, TextField, Button, Grid, MenuItem, Checkbox,
  IconButton, Paper, Divider, CircularProgress, Dialog,
  DialogTitle, DialogContent, DialogActions,
  Radio, RadioGroup, FormControlLabel, FormControl, FormLabel, Alert
} from "@mui/material";
import {
  Assignment as AssignmentIcon,
  CoPresent as CoPresentIcon,
  Payment as PaymentIcon,
  BusinessCenter as BusinessCenterIcon,
  Add as AddIcon, Delete as DeleteIcon,
  EditOutlined as EditIcon, ArrowForward as ArrowForwardIcon, ArrowBack as ArrowBackIcon, Close as CloseIcon
} from "@mui/icons-material";
import { useLocation, useNavigate } from "react-router-dom";
import Routings from "../../Routings";
import { postData, fetchDataById } from "../../BaseUrl";
import Toast from "../../DynamicComponent/Toast";
import CreditWaiverDialog from "./CreditWaiverDialog";
import BiliteracyWaiverDialog from "./BiliteracyWaiverDialog";
import registerBgImg from "../../Assest/Images/registerBgImg.svg";
import registerSideImg from "../../Assest/Images/registerSideImg.svg";
import VolunteerAgreement from "./Agreement/VolunteerAgreement"
import RefundPolicy from "./Agreement/RefundPolicy";
import StudyMediaConsent from "./Agreement/StudyMediaConsent";
import StudentWaiver from "./Agreement/StudentWaiver";
import InfoIcon from '@mui/icons-material/Info';

// ─── Palette ──────────────────────────────────────────────────────────────────
const NAVY = "#1d4e89";
const NAVY_DARK = "#163c6e";
const STEP_INACTIVE = "#a9c7e8";
const LINE_INACTIVE = "#cfe2f3";
const ORANGE = "#f59e0b";
const TITLE_BLUE = "#1f6fb2";
const LABEL = "#4a4a4a";
const RED = "#e53935";

// ─── Constants ────────────────────────────────────────────────────────────────
const GRADES = [
  "MunMazhalai", "Mazhalai",
  "Nilai 1", "Nilai 2", "Nilai 3", "Nilai 4",
  "Nilai 5", "Nilai 6", "Nilai 7", "Nilai 8",
  "Seal of Bi-literacy"
];
const CREDIT_WAIVER_GRADES = ["Nilai 6", "Nilai 7", "Nilai 8"];
const COUNSELOR_GRADES = ["Nilai 8", "Seal of Bi-literacy"];

// Grade order index for skip-grade detection
const GRADE_INDEX = Object.fromEntries(GRADES.map((g, i) => [g.toLowerCase(), i]));

const US_STATES = [
  "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA", "HI", "ID", "IL", "IN", "IA",
  "KS", "KY", "LA", "ME", "MD", "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
  "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC", "SD", "TN", "TX", "UT", "VT",
  "VA", "WA", "WV", "WI", "WY"
];

const COUNTIES = ["Forsyth", "Cobb", "Fulton", "Gwinnett"];

const STEP_LABELS = ["Student Registration", "Parent Details", "Payment", "Confirmation"];
const STEP_ICONS = [AssignmentIcon, CoPresentIcon, PaymentIcon, BusinessCenterIcon];
const SCREEN_TO_STEP = [0, 1, 2, 2, 3];   // internal screen → stepper index

const ZELLE = {
  type: "Business",
  name: "Atlanta Tamil School INC",
  email: "ctsfinance@cummingtamilschool.org"
};

const POLICIES = [
  {
    key: "policyVolunteerAgreement",
    title: "Volunteer Agreement",
    content: <VolunteerAgreement />
  },
  {
    key: "policyStudentWaiver",
    title: "Student Waiver and Release of Liability",
    content: <StudentWaiver />
  },
  {
    key: "policyMediaConsent",
    title: "Study Media Consent Form",
    content: <StudyMediaConsent />
  },
  {
    key: "policyRefundPolicy",
    title: "Refund Policy",
    content: <RefundPolicy />
  }
];

const INITIAL_FORM = {
  studentType: "new",
  existingRegistrationNumber: "",
  lookupParentEmail: "",
  previousGrade: "",
  requestForEvaluation: "",
  firstName: "", middleName: "", lastName: "",
  dob: "", gender: "", address: "", city: "", state: "", zip: "",
  gradeApplyingFor: "",
  creditWaiverAcknowledged: false,
  sealOfBiliteracy: false,
  county: "", countiesId: "", countiesSchoolName: "", counselorEmail: "",
  siblingAttending: false, siblings: [],
  parent1FirstName: "", parent1LastName: "", parent1Email: "", parent1Phone: "",
  parent2FirstName: "", parent2LastName: "", parent2Email: "", parent2Phone: "",
  emergencyContactName: "", emergencyContactPhone: "",
  policyVolunteerAgreement: false, policyStudentWaiver: false,
  policyMediaConsent: false, policyRefundPolicy: false, termsAccepted: false,
  payeeName: "", payeeEmail: "", payeeContactNumber: "", paymentInfo: "",
  zelleTransactionId: ""
};

const formatUSPhone = (val) => {
  if (!val) return "";
  const cleaned = val.replace(/\D/g, "");
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
  } else if (cleaned.length === 11 && cleaned.startsWith("1")) {
    return `+1${cleaned.slice(1)}`;
  }
  return val;
};

const Y = new Date().getFullYear();
const YEAR_TITLE = `${Y} -${String(Y + 1).slice(-2)}`;
const ACADEMIC = `${Y}–${String(Y + 1).slice(-2)}`;

const inputSx = {
  "& .MuiOutlinedInput-root": {
    borderRadius: "8px",
    backgroundColor: "#fff",
    fontSize: 14,
    "& fieldset": { borderColor: "#e2e2e2" },
    "&:hover fieldset": { borderColor: "#c9c9c9" },
    "&.Mui-focused fieldset": { borderColor: NAVY }
  },
  "& .MuiOutlinedInput-input::placeholder": { color: "#b3b3b3", opacity: 1 }
};

// ═════════════════════════════════════════════════════════════════════════════
//  Top-level stable presentational components
// ═════════════════════════════════════════════════════════════════════════════
function FancyStepper({ activeStep }) {
  return (
    <Box sx={{ display: "flex", alignItems: "flex-start", justifyContent: "center", maxWidth: 640, mx: "auto", mb: 1 }}>
      {STEP_LABELS.map((label, i) => {
        const Icon = STEP_ICONS[i];
        const done = i <= activeStep;
        return (
          <React.Fragment key={label}>
            <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", width: 96, flexShrink: 0 }}>
              <Box sx={{
                width: 44, height: 44, borderRadius: "50%",
                display: "flex", alignItems: "center", justifyContent: "center",
                bgcolor: done ? NAVY : "#fff",
                border: done ? "none" : `2px solid ${STEP_INACTIVE}`,
                color: done ? "#fff" : STEP_INACTIVE,
                transition: "all .25s"
              }}>
                <Icon sx={{ fontSize: 22 }} />
              </Box>
              <Typography sx={{
                fontSize: 13, mt: 1, textAlign: "center",
                color: done ? NAVY : STEP_INACTIVE,
                fontWeight: i === activeStep ? 600 : 500
              }}>
                {label}
              </Typography>
            </Box>
            {i < STEP_LABELS.length - 1 && (
              <Box sx={{
                flex: 1, height: 3, mt: "21px", mx: -0.5, borderRadius: 2,
                bgcolor: i < activeStep ? NAVY : LINE_INACTIVE
              }} />
            )}
          </React.Fragment>
        );
      })}
    </Box>
  );
}

const fieldLabel = (text, required) => (
  <Typography component="label" sx={{ display: "block", fontSize: 13, color: LABEL, mb: 0.6, fontWeight: 500 }}>
    {text}{required && <Box component="span" sx={{ color: RED, ml: 0.3 }}>*</Box>}
  </Typography>
);

const sectionHeader = (text) => (
  <Typography sx={{ color: ORANGE, fontSize: 12, fontWeight: 700, letterSpacing: 0.6, mb: 2, mt: 0.5 }}>
    {text}
  </Typography>
);

// ═════════════════════════════════════════════════════════════════════════════
//  Component
// ═════════════════════════════════════════════════════════════════════════════
export default function StudentRegistration() {
  const navigate = useNavigate();
  const location = useLocation()

  const [screen, setScreen] = useState(0);
  const [form, setForm] = useState(() => {
    let base = { ...INITIAL_FORM };
    if (location.state?.existingStudent) {
      base.studentType = "old";
      if (location.state.registrationNumber) {
        base.existingRegistrationNumber = location.state.registrationNumber;
      }
      if (location.state.studentData) {
        const { gradeApplyingFor: _skip, ...rest } = location.state.studentData;
        const prevGrade = location.state.studentData.completedGrade || location.state.studentData.previousGrade || location.state.studentData.gradeApplyingFor || "";
        base = {
          ...base,
          ...rest,
          gradeApplyingFor: "",          // never auto-filled
          previousGrade: prevGrade,      // read-only display field
          requestForEvaluation: "",      // reset — user must choose
        };
      }
    } else if (location.state?.studentType) {
      base.studentType = location.state.studentType;
    }
    return base;
  });

  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [lookupLoading, setLookupLoading] = useState(false);
  const [waiverOpen, setWaiverOpen] = useState(false);
  const [biliteracyWaiverOpen, setBiliteracyWaiverOpen] = useState(false);
  const [biliteracyCheckboxWaiverOpen, setBiliteracyCheckboxWaiverOpen] = useState(false);
  const [policyModal, setPolicyModal] = useState(null);
  const [paymentInfoOpen, setPaymentInfoOpen] = useState(false);
  const [toast, setToast] = useState({ open: false, severity: "success", message: "" });
  const [qrDialogOpen, setQrDialogOpen] = useState(false);

  React.useEffect(() => {
    const params = new URLSearchParams(location.search);
    if (params.get("type") === "QR") {
      setQrDialogOpen(true);
    }
  }, [location.search]);

  // ── helpers (called as functions, never as <JSX/> — avoids remount/focus loss)
  const formState = {
    firstName: {
      regex: /^.{0,40}$/,
      error: "First name cannot exceed 40 characters",
    },
    middleName: {
      regex: /^.{0,40}$/,
      error: "Middle name cannot exceed 40 characters",
    },
    lastName: {
      regex: /^.{0,40}$/,
      error: "Last name cannot exceed 40 characters",
    },
    zip: {
      regex: /^\d+$/,
      error: "Enter a valid ZIP code"
    },
    lookupParentEmail: {
      regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      error: "Enter a valid email address"
    },
    payeeEmail: {
      regex: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
      error: "Enter a valid email address"
    },
    payeeContactNumber: {
      regex: /^\d{0,15}$/,
      error: "Enter a valid phone number"
    }
  };

  const set = (field) => (e) => {
    const v = e.target.type === "checkbox" ? e.target.checked : e.target.value;

    setForm((p) => ({ ...p, [field]: v }));

    if (field === "dob" && v) {
      const today = new Date();
      const dobDate = new Date(v);
      let age = today.getFullYear() - dobDate.getFullYear();
      const m = today.getMonth() - dobDate.getMonth();
      if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
        age--;
      }
      if (age < 5 || age > 17) {
        setErrors((p) => ({ ...p, dob: "Age should be between 5 to 17" }));
        return;
      }
    }

    const validation = formState[field];

    // No validation configured => allow anything
    if (!validation?.regex) {
      setErrors((p) => ({ ...p, [field]: "" }));
      return;
    }

    setErrors((p) => ({
      ...p,
      [field]: validation.regex.test(v) ? "" : validation.error,
    }));
  };

  const errLine = (field) => errors[field]
    ? <Typography sx={{ fontSize: 11, color: RED, mt: 0.4 }}>{errors[field]}</Typography>
    : null;

  const textField = ({ label, field, required, placeholder, type, multiline, rows }) => (
    <Box sx={{ mb: 2.2 }}>
      {fieldLabel(label, required)}
      <TextField
        value={form[field]} onChange={set(field)}
        placeholder={placeholder} type={type || "text"}
        multiline={multiline} rows={rows}
        fullWidth size="small" error={!!errors[field]} sx={inputSx}
      />
      {errLine(field)}
    </Box>
  );

  const selField = ({ label, field, required, placeholder, options, onChange, disabled }) => (
    <Box sx={{ mb: 2.2 }}>
      {fieldLabel(label, required)}
      <TextField
        select value={form[field]} onChange={onChange || set(field)}
        fullWidth size="small" error={!!errors[field]} sx={inputSx}
        disabled={!!disabled}
        SelectProps={{
          displayEmpty: true,
          renderValue: (val) => val
            ? (options.find(o => (o.value ?? o) === val)?.label ?? val)
            : <span style={{ color: "#b3b3b3" }}>{placeholder}</span>
        }}
      >
        <MenuItem value="" disabled>{placeholder}</MenuItem>
        {options.map(o => {
          const value = o.value ?? o, lbl = o.label ?? o;
          return <MenuItem key={value} value={value}>{lbl}</MenuItem>;
        })}
      </TextField>
      {errLine(field)}
    </Box>
  );

  const previewField = ({ label, value, required }) => (
    <Box sx={{ mb: 2.5 }}>
      <Typography sx={{ fontSize: 12, color: "#9a9a9a", mb: 0.4 }}>
        {label}{required && <Box component="span" sx={{ color: RED, ml: 0.3 }}>*</Box>}
      </Typography>
      <Typography sx={{ fontSize: 14, color: "#2b2b2b", fontWeight: 500, whiteSpace: "pre-line" }}>
        {value || "—"}
      </Typography>
    </Box>
  );

  // ── county ───────────────────────────────────────────────────────────────
  const handleCounty = (e) => {
    const c = e.target.value;
    if (["Cobb", "Gwinnett"].includes(c)) {
      setToast({ open: true, severity: "error", message: "This County is not eligible" });
      setForm(p => ({ ...p, county: "", counselorEmail: "" }));
    } else {
      setForm(p => ({ ...p, county: c }));
      setErrors(p => ({ ...p, county: "" }));
    }
  };

  // ── grade ───────────────────────────────────────────────────────────────
  const handleGrade = (e) => {
    const g = e.target.value;
    // Reset requestForEvaluation on every grade change — re-evaluated by increment logic
    setForm(p => ({ ...p, gradeApplyingFor: g, creditWaiverAcknowledged: false, requestForEvaluation: "", sealOfBiliteracy: false }));
    setErrors(p => ({ ...p, gradeApplyingFor: "", requestForEvaluation: "" }));
    if (CREDIT_WAIVER_GRADES.includes(g) && form.studentType !== "old") {
      setWaiverOpen(true);
    } else if (g === "Seal of Bi-literacy") {
      setBiliteracyWaiverOpen(true);
    }
  };

  // ── siblings ───────────────────────────────────────────────────────────────
  const addSibling = () => setForm(p => ({ ...p, siblings: [...p.siblings, { name: "", grade: "" }] }));
  const removeSibling = (i) => setForm(p => ({ ...p, siblings: p.siblings.filter((_, x) => x !== i) }));
  const updSibling = (i, f, v) => setForm(p => {
    const s = [...p.siblings]; s[i] = { ...s[i], [f]: v }; return { ...p, siblings: s };
  });

  // ── lookup ───────────────────────────────────────────────────────────────
  const handleLookup = async () => {
    if (!form.existingRegistrationNumber && !form.lookupParentEmail) {
      setErrors(p => ({
        ...p,
        existingRegistrationNumber: "Enter registration number or parent email",
        lookupParentEmail: "Enter registration number or parent email"
      }));
      return;
    }
    setLookupLoading(true);
    try {
      const payload = {};
      if (form.existingRegistrationNumber) payload.registrationNumber = form.existingRegistrationNumber;
      if (form.lookupParentEmail) payload.parentEmail = form.lookupParentEmail;
      const r = await postData(
        `student/registration/lookup`, payload
      );
      if (r?.data) {
        // Auto-populate all fields EXCEPT gradeApplyingFor
        // previousGrade = last completed grade (API may return as completedGrade or gradeApplyingFor from last year)
        const { gradeApplyingFor: _skip, ...rest } = r.data;
        const prevGrade = r.data.completedGrade || r.data.previousGrade || r.data.gradeApplyingFor || "";
        setForm(p => ({
          ...p,
          ...rest,
          gradeApplyingFor: "",          // never auto-filled
          previousGrade: prevGrade,      // read-only display field
          requestForEvaluation: "",      // reset — user must choose
        }));
        setToast({ open: true, severity: "success", message: "Student data loaded. Please select the grade you are applying for." });
      }
    } catch {
      setToast({ open: true, severity: "error", message: "No student found with the provided details" });
    }
    setLookupLoading(false);
  };

  // ── grade-increment helpers ───────────────────────────────────────────────────
  // Only meaningful for old students who have a previousGrade set (after auto-fill)
  const gradeIncrement = (() => {
    if (form.studentType !== "old" || !form.previousGrade || !form.gradeApplyingFor) return 0;
    const prevIdx = form.previousGrade ? (GRADE_INDEX[form.previousGrade.toLowerCase()] ?? -1) : -1;
    const applyIdx = form.gradeApplyingFor ? (GRADE_INDEX[form.gradeApplyingFor.toLowerCase()] ?? -1) : -1;
    return applyIdx - prevIdx; // positive = upgrading, 0 = same, negative = downgrade
  })();

  const isNormalIncrement = gradeIncrement === 1;   // exactly +1 grade → show Yes/No radio
  const isSkipGrade = gradeIncrement > 1;     // +2 or more → auto Yes, no radio
  const evaluationMandatory = form.studentType === "new" || isSkipGrade;

  // ── policies ───────────────────────────────────────────────────────────────
  const agreedAll = form.policyVolunteerAgreement && form.policyStudentWaiver
    && form.policyMediaConsent && form.policyRefundPolicy && form.termsAccepted;

  // ── validation ───────────────────────────────────────────────────────────────
  const validate = (s) => {
    const e = {};
    const emailRe = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    const usPhoneRe = /^(?:\+?1[-. ]?)?\(?([0-9]{3})\)?[-. ]?([0-9]{3})[-. ]?([0-9]{4})$/;

    if (s === 0) {
      const requiredFields = [
        "firstName", "lastName", "dob", "gender", "address", "city", "state", "zip",
        "gradeApplyingFor", "county", "countiesId", "countiesSchoolName"
      ];
      requiredFields.forEach(f => { if (!form[f]) e[f] = "Required"; });

      if (form.dob) {
        const today = new Date();
        const dobDate = new Date(form.dob);
        let age = today.getFullYear() - dobDate.getFullYear();
        const m = today.getMonth() - dobDate.getMonth();
        if (m < 0 || (m === 0 && today.getDate() < dobDate.getDate())) {
          age--;
        }
        if (age < 5 || age > 17) {
          e.dob = "Age should be between 5 to 17";
        }
      }
      if (COUNSELOR_GRADES.includes(form.gradeApplyingFor)) {
        if (!form.counselorEmail) e.counselorEmail = "Required";
        else if (!emailRe.test(form.counselorEmail)) e.counselorEmail = "Invalid email";
      }
      if (form.studentType === "old" && !form.existingRegistrationNumber)
        e.existingRegistrationNumber = "Required for returning students";
      // Request for Evaluation — required only when old student + exactly +1 grade increment
      if (form.studentType === "old" && isNormalIncrement && !form.requestForEvaluation)
        e.requestForEvaluation = "Please select Yes or No for Request for Evaluation";
      if (form.siblingAttending) form.siblings.forEach((sb, i) => {
        if (!sb.name) e[`sib_name_${i}`] = "Required";
        if (!sb.grade) e[`sib_grade_${i}`] = "Required";
      });
    }
    if (s === 1) {
      ["parent1FirstName", "parent1LastName", "parent1Email", "parent1Phone",
        "parent2FirstName", "parent2LastName", "parent2Email", "parent2Phone",
        "emergencyContactName", "emergencyContactPhone"
      ].forEach(f => { if (!form[f]) e[f] = "Required"; });
      if (form.parent1Email && !emailRe.test(form.parent1Email)) e.parent1Email = "Invalid email";
      if (form.parent2Email && !emailRe.test(form.parent2Email)) e.parent2Email = "Invalid email";
      if (form.parent1Phone && !usPhoneRe.test(form.parent1Phone)) e.parent1Phone = "Invalid US phone number (e.g. 770-555-1234)";
      if (form.parent2Phone && !usPhoneRe.test(form.parent2Phone)) e.parent2Phone = "Invalid US phone number (e.g. 770-555-1234)";
      if (form.emergencyContactPhone && !usPhoneRe.test(form.emergencyContactPhone))
        e.emergencyContactPhone = "Invalid US phone number (e.g. 770-555-1234)";
    }
    if (s === 2) {
      if (!agreedAll) e._agree = "Please accept all policies and the Terms & Conditions to continue";
    }
    if (s === 3) {
      ["payeeName", "payeeEmail", "payeeContactNumber", "zelleTransactionId"].forEach(f => { if (!form[f]) e[f] = "Required"; });
      if (form.payeeEmail && !emailRe.test(form.payeeEmail)) e.payeeEmail = "Invalid email";
      if (form.payeeContactNumber && !usPhoneRe.test(form.payeeContactNumber)) e.payeeContactNumber = "Invalid US phone number (e.g. 770-555-1234)";
      if (form.zelleTransactionId && !/^[a-zA-Z0-9]{6,20}$/.test(form.zelleTransactionId)) {
        e.zelleTransactionId = "Invalid transaction ID (6-20 alphanumeric characters)";
      }
    }
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // ── navigation ───────────────────────────────────────────────────────────────
  const next = () => {
    if (validate(screen)) {
      if (screen === 1) {
        setForm(p => ({
          ...p,
          parent1Phone: formatUSPhone(p.parent1Phone),
          parent2Phone: formatUSPhone(p.parent2Phone),
          emergencyContactPhone: formatUSPhone(p.emergencyContactPhone)
        }));
      }
      if (screen === 3) {
        setForm(p => ({
          ...p,
          payeeContactNumber: formatUSPhone(p.payeeContactNumber)
        }));
      }
      setScreen(s => s + 1);
    }
  };
  const cancel = () => {
    setErrors({});
    if (screen === 0) navigate(Routings.HOME || "/");
    else setScreen(s => s - 1);
  };

  // ── submit ───────────────────────────────────────────────────────────────
  const submit = async () => {
    setLoading(true);
    try {
      const fd = new FormData();
      const phoneFields = ["parent1Phone", "parent2Phone", "emergencyContactPhone", "payeeContactNumber"];
      Object.entries(form).forEach(([k, v]) => {
        if (k === "paymentInfo") return;
        if (k === "siblings") {
          fd.append(k, JSON.stringify(v));
        } else if (phoneFields.includes(k) && typeof v === "string") {
          const formatted = formatUSPhone(v);
          if (formatted !== "") {
            fd.append(k, formatted);
          }
        } else if (k === "creditWaiverAcknowledged" && CREDIT_WAIVER_GRADES.includes(form.gradeApplyingFor)) {
          fd.append(k, true);
        } else if (v !== null && v !== undefined && v !== "") {
          fd.append(k, v);
        }
      });
      // skipGrade flag — true only when old student jumps 2+ grades
      fd.set("skipGrade", isSkipGrade ? "true" : "false");
      // Resolve final requestForEvaluation value
      //   new student       → always true
      //   skip grade (2+)   → always true
      //   normal increment  → user choice (form.requestForEvaluation === "yes")
      //   no grade context  → false
      const resolvedEval =
        form.studentType === "new" || isSkipGrade
          ? true
          : form.requestForEvaluation === "yes";
      fd.set("requestForEvaluation", resolvedEval);
      const r = await postData("student/registration/submit", fd);
      setToast({ open: true, severity: "success", message: "Application submitted successfully!" });
      setTimeout(() => navigate(Routings.REGISTRATION_SUCCESS || "/", {
        state: { applicationNumber: r.data?.applicationNumber }
      }), 1100);
    } catch (err) {
      let message;
      if (!err?.response) {
        message = "Cannot reach the server. Please check your internet connection and try again.";
      } else {
        message = err.response.data?.message || "Submission failed. Please try again.";
      }
      setToast({ open: true, severity: "error", message });
    }
    setLoading(false);
  };

  // ═══════════════════════════════════════════════════════════════════════
  //  SCREEN RENDERERS (plain functions — invoked inline)
  // ═══════════════════════════════════════════════════════════════════════
  const renderScreen0 = () => (
    <>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e" }}>Registration</Typography>
      <Typography sx={{ fontSize: 13, color: "#999", mb: 2.5 }}>
        Academic Year {ACADEMIC} · Fill all required fields
      </Typography>

      {sectionHeader("STUDENT INFORMATION")}

      <Grid container columnSpacing={3}>

        {form.studentType === "old" && (
          <Grid item xs={12}>
            <Grid container columnSpacing={2} alignItems="flex-start">
              {/* Registration Number */}
              <Grid item xs={12} sm={5}>
                <Box sx={{ mb: 2.2 }}>
                  {fieldLabel("Tamil School ID")}
                  <TextField
                    value={form.existingRegistrationNumber} onChange={set("existingRegistrationNumber")}
                    placeholder="Enter registration number"
                    fullWidth size="small" error={!!errors.existingRegistrationNumber} sx={inputSx}
                  />
                  {errLine("existingRegistrationNumber")}
                </Box>
              </Grid>

              {/* OR divider */}
              <Grid item xs={12} sm={1} sx={{ display: "flex", alignItems: "center", justifyContent: "center", pt: { sm: 3.5 }, pb: { xs: 1, sm: 0 } }}>
                <Box sx={{ width: 28, height: 28, borderRadius: "50%", bgcolor: "#f0f0f0", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Typography sx={{ fontSize: 11, fontWeight: 700, color: "#999" }}>OR</Typography>
                </Box>
              </Grid>

              {/* Parent Email */}
              <Grid item xs={12} sm={5}>
                <Box sx={{ mb: 2.2 }}>
                  {fieldLabel("Parent Email")}
                  <TextField
                    value={form.lookupParentEmail} onChange={set("lookupParentEmail")}
                    placeholder="Enter parent email address"
                    type="email"
                    fullWidth size="small" error={!!errors.lookupParentEmail} sx={inputSx}
                  />
                  {errLine("lookupParentEmail")}
                </Box>
              </Grid>

              {/* Auto-Fill Button */}
              <Grid item xs={12} sm={1} sx={{ display: "flex", alignItems: "flex-start", pt: { sm: 2.8 } }}>
                <Button size="small" onClick={handleLookup} disabled={lookupLoading} variant="outlined" fullWidth
                  sx={{ whiteSpace: "nowrap", borderColor: NAVY, color: NAVY, textTransform: "none", borderRadius: "8px", py: 0.9 }}>
                  {lookupLoading ? <CircularProgress size={18} /> : "Auto-Fill"}
                </Button>
              </Grid>
            </Grid>
          </Grid>
        )}



        <Grid item xs={12} sm={6}>{textField({ label: "Student First Name", field: "firstName", required: true, placeholder: "Enter first name" })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "Student Middle Name", field: "middleName", placeholder: "Optional" })}</Grid>

        <Grid item xs={12} sm={6}>{textField({ label: "Student Last Name", field: "lastName", required: true, placeholder: "Enter last name" })}</Grid>
        <Grid item xs={12} sm={6}>{selField({
          label: "Student Type", field: "studentType", required: true, placeholder: "Select Student Type",
          options: [{ value: "new", label: "New Student" }, { value: "old", label: "Existing Student" }],
          disabled: true
        })}</Grid>



        <Grid item xs={12} sm={6}>
          <Box sx={{ mb: 2.2 }}>
            {fieldLabel("Date of Birth", true)}
            <TextField
              value={form.dob} onChange={set("dob")} type="date"
              fullWidth size="small" error={!!errors.dob} sx={inputSx}
              InputLabelProps={{ shrink: true }}
              inputProps={{ max: new Date().toISOString().split("T")[0], }}
            />
            {errLine("dob")}
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>{selField({ label: "Gender", field: "gender", required: true, placeholder: "Select", options: ["Male", "Female", "Prefer not to say"] })}</Grid>

        <Grid item xs={12} sm={6}>{textField({ label: "Address", field: "address", required: true, placeholder: "Enter Address", multiline: true, rows: 4 })}</Grid>
        <Grid item xs={12} sm={6}>
          {/* ── Previous Grade (read-only) — shown for old students ── */}
          {form.studentType === "old" && (
            <Box sx={{ mb: 2.2 }}>
              {fieldLabel("Previous Grade (Last Completed)")}
              <TextField
                value={form.previousGrade || ""} disabled
                placeholder="Auto-populated after Auto-Fill"
                fullWidth size="small" sx={{
                  ...inputSx,
                  "& .MuiOutlinedInput-root": {
                    ...inputSx["& .MuiOutlinedInput-root"],
                    bgcolor: "#f5f5f5",
                  }
                }}
              />
              <Typography sx={{ fontSize: 11, color: "#888", mt: 0.4 }}>
                Auto-derived from last academic year — not editable
              </Typography>
            </Box>
          )}

          {selField({ label: "Applying Grade", field: "gradeApplyingFor", required: true, placeholder: "Select", options: GRADES, onChange: handleGrade })}
          {form.gradeApplyingFor === "Nilai 8" && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: { sm: 3.5 } }}>
              <Checkbox
                checked={!!form.sealOfBiliteracy}
                onChange={(e) => {
                  if (e.target.checked) {
                    setBiliteracyCheckboxWaiverOpen(true);
                  } else {
                    setForm(p => ({ ...p, sealOfBiliteracy: false }));
                  }
                }}
                sx={{ p: 0.5, color: NAVY, "&.Mui-checked": { color: NAVY } }}
              />
              <Typography sx={{ fontSize: 14, color: "#333" }}>Are you choosing Seal of Biliteracy?</Typography>
            </Box>
          )}
        </Grid>

        {/* ── Request for Evaluation card ──
             Conditions:
             - Old student only
             - Grade must have been selected (gradeApplyingFor is set)
             - previousGrade must be known (auto-fill done)
             Show radio   → exactly +1 grade increment (isNormalIncrement)
             Show auto-yes info → skip grade 2+ (isSkipGrade)
        ── */}
        {form.studentType === "old" && form.previousGrade && form.gradeApplyingFor && (isNormalIncrement || isSkipGrade) && (
          <Grid item xs={12}>
            <Box sx={{
              mb: 2.2, p: 2, borderRadius: "10px",
              border: `1.5px solid ${isSkipGrade ? RED : errors.requestForEvaluation ? RED : NAVY}`,
              bgcolor: isSkipGrade ? "#fff8f8" : "#f0f5fb"
            }}>
              <FormControl component="fieldset" fullWidth>
                <FormLabel component="legend" sx={{
                  fontSize: 13, fontWeight: 700,
                  color: isSkipGrade ? RED : NAVY, mb: 0.5,
                  "&.Mui-focused": { color: NAVY }
                }}>
                  Request for Evaluation
                  {isSkipGrade && (
                    <Box component="span" sx={{
                      ml: 1, fontSize: 11, color: "#fff", fontWeight: 700,
                      bgcolor: RED, px: 1, py: 0.25, borderRadius: "4px"
                    }}>
                      AUTO YES — SKIP GRADE
                    </Box>
                  )}
                  {isNormalIncrement && (
                    <Box component="span" sx={{
                      ml: 1, fontSize: 11, color: NAVY, fontWeight: 600,
                      bgcolor: "#dce8f7", px: 1, py: 0.25, borderRadius: "4px"
                    }}>
                      REQUIRED
                    </Box>
                  )}
                </FormLabel>

                {isSkipGrade ? (
                  /* Skip grade: 2+ levels — no user choice, automatically YES */
                  <Alert severity="error" sx={{ mt: 0.5, fontSize: 12, py: 0.5 }}>
                    <strong>Evaluation is mandatory.</strong> The selected grade
                    &nbsp;<strong>{form.gradeApplyingFor}</strong>&nbsp; skips
                    &nbsp;<strong>{gradeIncrement - 1}</strong>&nbsp;level(s) from the previous grade
                    &nbsp;<strong>({form.previousGrade})</strong>.
                    The system will automatically initiate the evaluation workflow.
                  </Alert>
                ) : (
                  /* Normal +1 increment — user chooses */
                  <RadioGroup
                    row
                    value={form.requestForEvaluation}
                    onChange={(e) => {
                      setForm(p => ({ ...p, requestForEvaluation: e.target.value }));
                      setErrors(p => ({ ...p, requestForEvaluation: "" }));
                    }}
                  >
                    <FormControlLabel
                      value="yes"
                      control={<Radio sx={{ color: NAVY, "&.Mui-checked": { color: NAVY } }} />}
                      label={<Typography sx={{ fontSize: 14, fontWeight: 500 }}>Yes — Request Evaluation</Typography>}
                    />
                    <FormControlLabel
                      value="no"
                      control={<Radio sx={{ color: NAVY, "&.Mui-checked": { color: NAVY } }} />}
                      label={<Typography sx={{ fontSize: 14, fontWeight: 500 }}>No — Skip Evaluation</Typography>}
                    />
                  </RadioGroup>
                )}
              </FormControl>

              {errors.requestForEvaluation && (
                <Typography sx={{ fontSize: 11, color: RED, mt: 0.5 }}>{errors.requestForEvaluation}</Typography>
              )}
              {form.requestForEvaluation === "yes" && isNormalIncrement && (
                <Typography sx={{ fontSize: 11, color: "#1d7a3e", mt: 0.5, fontWeight: 500 }}>
                  ✓ Evaluation workflow will be initiated after submission.
                </Typography>
              )}
              {form.requestForEvaluation === "no" && isNormalIncrement && (
                <Typography sx={{ fontSize: 11, color: "#888", mt: 0.5 }}>
                  No evaluation process will be triggered.
                </Typography>
              )}
            </Box>
          </Grid>
        )}

        <Grid item xs={12} sm={6}>{textField({ label: "City", field: "city", required: true, placeholder: "Select City" })}</Grid>
        <Grid item xs={12} sm={6}>{selField({ label: "State", field: "state", required: true, placeholder: "Select State", options: US_STATES })}</Grid>

        <Grid item xs={12} sm={6}>{textField({ label: "Zip Code", field: "zip", required: true, placeholder: "Enter Zip Code" })}</Grid>

        <Grid item xs={12} sm={6}>{selField({ label: "County", field: "county", required: true, placeholder: "Select County", options: COUNTIES, onChange: handleCounty })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "County ID", field: "countiesId", required: true, placeholder: "Enter County ID" })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "County School Name", field: "countiesSchoolName", required: true, placeholder: "Enter County School Name" })}</Grid>
        {/* {COUNSELOR_GRADES.includes(form.gradeApplyingFor) && (
          <Grid item xs={12} sm={6}>{textField({ label: "County School ID", field: "countySchoolId", required: true, placeholder: "Enter County School ID" })}</Grid>
        )} */}
        {COUNSELOR_GRADES.includes(form.gradeApplyingFor) && (
          <Grid item xs={12} sm={6}>{textField({ label: "Counselor Email", field: "counselorEmail", required: true, placeholder: "Enter Counselor Email" })}</Grid>
        )}
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: { sm: 3.5 } }}>
            <Typography sx={{ fontSize: 14, color: "#333" }}>Sibling Attending</Typography>
            <Checkbox checked={form.siblingAttending} onChange={set("siblingAttending")}
              sx={{ p: 0.5, color: NAVY, "&.Mui-checked": { color: NAVY } }} />
          </Box>
        </Grid>
      </Grid>

      {form.siblingAttending && (
        <Box sx={{ mt: 1 }}>
          {form.siblings.map((sb, i) => (
            <Grid container spacing={1} key={i} sx={{ mb: 1, alignItems: "center" }}>
              <Grid item xs={5}>
                <TextField placeholder="Sibling Name" value={sb.name} size="small" fullWidth sx={inputSx}
                  error={!!errors[`sib_name_${i}`]} onChange={(e) => updSibling(i, "name", e.target.value)} />
              </Grid>
              <Grid item xs={5}>
                <TextField select value={sb.grade} size="small" fullWidth sx={inputSx}
                  SelectProps={{ displayEmpty: true, renderValue: (v) => v || <span style={{ color: "#b3b3b3" }}>Grade</span> }}
                  error={!!errors[`sib_grade_${i}`]} onChange={(e) => updSibling(i, "grade", e.target.value)}>
                  {GRADES.map(g => <MenuItem key={g} value={g}>{g}</MenuItem>)}
                </TextField>
              </Grid>
              <Grid item xs={2}>
                <IconButton color="error" size="small" onClick={() => removeSibling(i)}><DeleteIcon /></IconButton>
              </Grid>
            </Grid>
          ))}
          <Button startIcon={<AddIcon />} onClick={addSibling} size="small"
            sx={{ textTransform: "none", color: NAVY, mt: 0.5 }}>Add Sibling</Button>
        </Box>
      )}
    </>
  );

  const renderScreen1 = () => (
    <>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e" }}>Registration</Typography>
      <Typography sx={{ fontSize: 13, color: "#999", mb: 2.5 }}>
        Academic Year {ACADEMIC} · Fill all required fields
      </Typography>

      {sectionHeader("PARENT INFORMATION")}

      <Grid container columnSpacing={3}>
        <Grid item xs={12} sm={6}>{textField({ label: "Parent 1 First Name", field: "parent1FirstName", required: true, placeholder: "Enter first name" })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "Parent 1 Last Name", field: "parent1LastName", required: true, placeholder: "Enter Last Name" })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "Parent 1 Email", field: "parent1Email", required: true, placeholder: "Enter Parent Email" })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "Parent 1 Phone Number", field: "parent1Phone", required: true, placeholder: "Enter Parent Phone Number" })}</Grid>

        <Grid item xs={12} sm={6}>{textField({ label: "Parent 2 First Name", field: "parent2FirstName", required: true, placeholder: "Enter first name" })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "Parent 2 Last Name", field: "parent2LastName", required: true, placeholder: "Enter Last Name" })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "Parent 2 Email", field: "parent2Email", required: true, placeholder: "Enter Parent Email" })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "Parent 2 Phone Number", field: "parent2Phone", required: true, placeholder: "Enter Parent Phone Number" })}</Grid>

        <Grid item xs={12} sm={6}>{textField({ label: "Emergency Contact Name", field: "emergencyContactName", required: true, placeholder: "Enter Emergency Contact Name" })}</Grid>
        <Grid item xs={12} sm={6}>{textField({ label: "Emergency Contact Phone", field: "emergencyContactPhone", required: true, placeholder: "Enter Emergency Contact Phone" })}</Grid>
      </Grid>
    </>
  );

  const renderScreen2 = () => (
    <>
      <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e", mb: 3 }}>Policy Section</Typography>

      {errors._agree && <Typography sx={{ fontSize: 13, color: RED, mb: 2 }}>{errors._agree}</Typography>}

      {POLICIES.map(p => (
        <Box key={p.key} sx={{ display: "flex", alignItems: "center", mb: 2.5 }}>
          <Checkbox
            checked={form[p.key]}
            onChange={() => {
              if (form[p.key]) {
                // unchecking a policy → also uncheck termsAccepted
                setForm(prev => ({ ...prev, [p.key]: false, termsAccepted: false }));
              } else {
                setPolicyModal(p);
              }
            }}
            sx={{ p: 0, mr: 2, color: "#9e9e9e", "&.Mui-checked": { color: NAVY } }}
          />
          <Typography onClick={() => setPolicyModal(p)}
            sx={{ fontSize: 16, color: NAVY, fontWeight: 600, cursor: "pointer", "&:hover": { textDecoration: "underline" } }}>
            {p.title}
          </Typography>
        </Box>
      ))}

      <Box sx={{ display: "flex", alignItems: "center", mt: 4 }}>
        <Checkbox checked={form.termsAccepted} onChange={set("termsAccepted")}
          sx={{ p: 0, mr: 2, color: "#9e9e9e", "&.Mui-checked": { color: NAVY } }} />
        <Typography sx={{ fontSize: 15, color: "#333", fontWeight: 500 }}>
          &ldquo;I Agree to Terms &amp; Conditions&rdquo;
        </Typography>
      </Box>

      <Dialog open={!!policyModal} onClose={() => setPolicyModal(null)} maxWidth="sm" sx={{ zIndex: 9999999999999 }} fullWidth>
        <DialogTitle sx={{ fontWeight: 700, color: NAVY }}>{policyModal?.title}</DialogTitle>
        <DialogContent dividers>
          <Box sx={{ lineHeight: 1.8 }}>
            {policyModal?.content}
          </Box>
        </DialogContent>
        <DialogActions sx={{ p: 2 }}>
          {policyModal && !form[policyModal.key] && (
            <Button onClick={() => {
              const updatedKey = policyModal.key;
              setForm(prev => {
                const next = { ...prev, [updatedKey]: true };
                // auto-check termsAccepted if all 4 policies are now accepted
                const allAccepted = POLICIES.every(pol => pol.key === updatedKey ? true : next[pol.key]);
                return { ...next, termsAccepted: allAccepted };
              });
              setPolicyModal(null);
            }}
              variant="contained" sx={{ bgcolor: NAVY, textTransform: "none", "&:hover": { bgcolor: NAVY_DARK } }}>
              Accept &amp; Close
            </Button>
          )}
          <Button onClick={() => setPolicyModal(null)} sx={{ textTransform: "none", color: "#666" }}>Close</Button>
        </DialogActions>
      </Dialog>
    </>
  );

  const PAYMENT_DEADLINE = new Date(new Date().getFullYear(), 7 /* Aug = 7 */, 7, 18, 30, 0);
  const isAfterDeadline = new Date() > PAYMENT_DEADLINE;
  const renderScreen3 = () => {
    // Aug 7 of current year, 6:30 PM local time

    return (
      <>
        <Box sx={{ display: "flex", alignItems: "center", justifyContent: "space-between", mb: 0.5 }}>
          <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e" }}>Make Payment</Typography>
          {/* <Typography
            onClick={() => setPaymentInfoOpen(true)}
            sx={{
              fontSize: 15, color: NAVY, fontWeight: 600, cursor: "pointer",
              textDecoration: "underline", "&:hover": { color: NAVY_DARK }
            }}
          >
            <InfoIcon sx={{ fontSize: 16, mr: 0.2, mb: -0.4 }} /> Payment Info
          </Typography> */}
        </Box>
        <Typography sx={{ fontSize: 13, color: "#999", mb: 2.5 }}>Complete your payment via Zelle to finalise.</Typography>

        <Box sx={{ mb: 0.8 }}>
          <Typography component="span" sx={{ fontSize: 15, color: NAVY, fontWeight: 600 }}>Recipient Type: </Typography>
          <Typography component="span" sx={{ fontSize: 15, color: "#333" }}>{ZELLE.type}</Typography>
        </Box>
        <Box sx={{ mb: 0.8 }}>
          <Typography component="span" sx={{ fontSize: 15, color: NAVY, fontWeight: 600 }}>Name: </Typography>
          <Typography component="span" sx={{ fontSize: 15, color: "#333" }}>{ZELLE.name}</Typography>
        </Box>
        <Box sx={{ mb: 2.5 }}>
          <Typography component="span" sx={{ fontSize: 15, color: NAVY, fontWeight: 600 }}>Email address: </Typography>
          <Typography component="a" href={`mailto:${ZELLE.email}`}
            sx={{ fontSize: 15, color: NAVY, textDecoration: "underline" }}>{ZELLE.email}</Typography>
        </Box>

        <Box sx={{ border: "1px solid #f0d9a8", backgroundColor: "#fdf6e8", borderRadius: "10px", p: 2.5, mb: 3 }}>
          <Typography sx={{ fontSize: 11, color: "#b08930", letterSpacing: 0.6, fontWeight: 700 }}>PAYMENT METHOD</Typography>
          <Typography sx={{ fontSize: 16, fontWeight: 700, color: "#1a1a2e", mb: 0.5 }}>Zelle Transfer</Typography>
          <Typography sx={{ fontSize: 13, color: "#6b6b6b", lineHeight: 1.6 }}>
            Send payment to the school&rsquo;s registered Zelle account. Once the transfer is complete,
            enter the Transaction ID below to link it to your registration.
          </Typography>
        </Box>

        <Grid container columnSpacing={3}>
          <Grid item xs={12} sm={6}>{textField({ label: "Name of payee", field: "payeeName", required: true, placeholder: "Enter name" })}</Grid>
          <Grid item xs={12} sm={6}>{textField({ label: "Email of Payee", field: "payeeEmail", required: true, placeholder: "Enter email" })}</Grid>
          <Grid item xs={12} sm={6}>{textField({ label: "Contact Number of payee", field: "payeeContactNumber", required: true, placeholder: "Enter Contact Number" })}</Grid>
          <Grid item xs={12}>{textField({ label: "Zelle Transaction ID", field: "zelleTransactionId", required: true, placeholder: "Enter transaction ID after payment" })}</Grid>
        </Grid>

        {/* ── Payment Info Dialog ── */}
        <Dialog open={paymentInfoOpen} onClose={() => setPaymentInfoOpen(false)} maxWidth="sm" fullWidth sx={{ zIndex: 9999 }}>
          <DialogTitle sx={{ fontWeight: 700, color: NAVY, pb: 1 }}>Payment Information</DialogTitle>
          <DialogContent dividers>
            {/* ── Before deadline ── */}
            <Box sx={{
              mb: 3, p: 2, borderRadius: "10px",
              border: isAfterDeadline ? "1px solid #e0e0e0" : "2px solid #1d4e89",
              bgcolor: isAfterDeadline ? "#fafafa" : "#eef4fb"
            }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: ORANGE, letterSpacing: 0.6, mb: 1 }}>
                BEFORE AUG 7, 6:30 PM
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
                <Typography sx={{ fontSize: 14, color: "#333" }}>Munmazhalai through Nilai 7</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>$220</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 14, color: "#333" }}>
                  Nilai 8 &nbsp;<Typography component="span" sx={{ fontSize: 12, color: "#888" }}>($250 + $30 Graduation Exp.)</Typography>
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>$280</Typography>
              </Box>
            </Box>

            {/* ── After deadline ── */}
            <Box sx={{
              p: 2, borderRadius: "10px",
              border: isAfterDeadline ? "2px solid #1d4e89" : "1px solid #e0e0e0",
              bgcolor: isAfterDeadline ? "#eef4fb" : "#fafafa"
            }}>
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: RED, letterSpacing: 0.6, mb: 1 }}>
                PAYMENT INFO — AFTER AUG 7, 6:30 PM
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
                <Typography sx={{ fontSize: 14, color: "#333" }}>
                  Munmazhalai through Nilai 7 &nbsp;<Typography component="span" sx={{ fontSize: 12, color: "#888" }}>($220 + $5 Shipping)</Typography>
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>$205</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 14, color: "#333" }}>
                  Nilai 8 &nbsp;<Typography component="span" sx={{ fontSize: 12, color: "#888" }}>($250 + $30 Graduation + $5 Shipping)</Typography>
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>$285</Typography>
              </Box>
            </Box>

            {isAfterDeadline && (
              <Typography sx={{ mt: 2, fontSize: 12, color: RED, fontWeight: 600 }}>
                ⚠️ The early registration deadline has passed. Shipping fee of $5 now applies.
              </Typography>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 2 }}>
            <Button onClick={() => setPaymentInfoOpen(false)}
              variant="contained" sx={{ bgcolor: NAVY, textTransform: "none", "&:hover": { bgcolor: NAVY_DARK } }}>
              Got it
            </Button>
          </DialogActions>
        </Dialog>
      </>
    );
  };

  const renderScreen4 = () => (
    <>
      <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Typography sx={{ fontSize: 20, fontWeight: 700, color: "#1a1a2e" }}>Registration Preview</Typography>
        <Button startIcon={<EditIcon sx={{ fontSize: 18 }} />} onClick={() => setScreen(0)}
          sx={{ textTransform: "none", color: NAVY, fontWeight: 600 }}>Edit</Button>
      </Box>

      {sectionHeader("STUDENT INFORMATION")}
      <Grid container columnSpacing={3}>
        <Grid item xs={12} sm={4}>{previewField({ label: "First Name", value: form.firstName, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Middle Name", value: form.middleName })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Last Name", value: form.lastName, required: true })}</Grid>

        <Grid item xs={12} sm={4}>{previewField({ label: "Student Type", value: form.studentType === "new" ? "New Student" : "Existing Student", required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Date of Birth", value: form.dob, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Gender", value: form.gender, required: true })}</Grid>

        <Grid item xs={12} sm={4}>{previewField({ label: "Address", value: form.address, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Next Grade (Applying For)", value: form.gradeApplyingFor, required: true })}</Grid>
        {form.previousGrade && (
          <Grid item xs={12} sm={4}>{previewField({ label: "Previous Grade (Last Completed)", value: form.previousGrade })}</Grid>
        )}

        <Grid item xs={12} sm={4}>{previewField({ label: "City", value: form.city, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "State", value: form.state, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Zip Code", value: form.zip, required: true })}</Grid>

        <Grid item xs={12} sm={4}>{previewField({ label: "County", value: form.county, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "County ID", value: form.countiesId, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "County School Name", value: form.countiesSchoolName, required: true })}</Grid>
        {COUNSELOR_GRADES.includes(form.gradeApplyingFor) && (
          <Grid item xs={12} sm={4}>{previewField({ label: "Counselor Email", value: form.counselorEmail, required: true })}</Grid>
        )}
      </Grid>

      <Box sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
        <Typography sx={{ fontSize: 14, color: "#333" }}>Sibling Attending</Typography>
        <Checkbox checked={form.siblingAttending} disabled size="small"
          sx={{ p: 0.5, "&.Mui-disabled": { color: NAVY } }} />
      </Box>
      {form.siblingAttending && form.siblings.length > 0 && (
        <Typography sx={{ fontSize: 13, color: "#555", mb: 1 }}>
          {form.siblings.map(s => `${s.name} (${s.grade})`).join(", ")}
        </Typography>
      )}

      {/* Request for Evaluation preview — old students only */}
      {form.studentType === "old" && (
        <Box sx={{
          mb: 2.5, p: 2, borderRadius: "10px",
          border: `1.5px solid ${NAVY}`, bgcolor: "#f0f5fb",
          display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap"
        }}>
          <Typography sx={{ fontSize: 13, fontWeight: 700, color: NAVY, minWidth: 180 }}>
            Request for Evaluation:
          </Typography>
          <Box sx={{
            px: 1.5, py: 0.4, borderRadius: "6px", fontWeight: 700, fontSize: 13,
            bgcolor: (evaluationMandatory || form.requestForEvaluation === "yes") ? "#e8f5e9" : "#fff3e0",
            color: (evaluationMandatory || form.requestForEvaluation === "yes") ? "#2e7d32" : "#e65100",
            border: `1px solid ${(evaluationMandatory || form.requestForEvaluation === "yes") ? "#a5d6a7" : "#ffcc80"}`
          }}>
            {evaluationMandatory
              ? `Yes — Mandatory (${isSkipGrade ? "Skip Grade" : "New Registration"})`
              : form.requestForEvaluation === "yes"
                ? "Yes — Evaluation Requested"
                : form.requestForEvaluation === "no"
                  ? "No — Skip Evaluation"
                  : "—"}
          </Box>
        </Box>
      )}

      {sectionHeader("PARENT INFORMATION")}
      <Grid container columnSpacing={3}>
        <Grid item xs={12} sm={4}>{previewField({ label: "Parent 1 First Name", value: form.parent1FirstName, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Parent 1 Last Name", value: form.parent1LastName, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Parent 1 Email", value: form.parent1Email, required: true })}</Grid>

        <Grid item xs={12} sm={4}>{previewField({ label: "Parent 1 Phone Number", value: form.parent1Phone, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Parent 2 First Name", value: form.parent2FirstName, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Parent 2 Last Name", value: form.parent2LastName, required: true })}</Grid>

        <Grid item xs={12} sm={4}>{previewField({ label: "Parent 2 Email", value: form.parent2Email, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Parent 2 Phone Number", value: form.parent2Phone, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Emergency", value: form.emergencyContactName, required: true })}</Grid>

        <Grid item xs={12} sm={4}>{previewField({ label: "Emergency Contact Phone", value: form.emergencyContactPhone, required: true })}</Grid>
      </Grid>

      {sectionHeader("PAYMENT DETAILS")}
      <Grid container columnSpacing={3}>
        <Grid item xs={12} sm={4}>{previewField({ label: "Payee Name", value: form.payeeName, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Payee Email", value: form.payeeEmail, required: true })}</Grid>
        <Grid item xs={12} sm={4}>{previewField({ label: "Payee Contact Number", value: form.payeeContactNumber, required: true })}</Grid>

        <Grid item xs={12} sm={6}>{previewField({ label: "Zelle Transaction ID", value: form.zelleTransactionId, required: true })}</Grid>
      </Grid>
    </>
  );

  const screenRenderers = [renderScreen0, renderScreen1, renderScreen2, renderScreen3, renderScreen4];

  // ── footer ───────────────────────────────────────────────────────────────
  const renderFooter = () => {
    const cancelBtn = (
      <Button variant="outlined" onClick={cancel}
        sx={{ borderColor: "#d0d0d0", color: "#555", textTransform: "none", px: 4, py: 1, borderRadius: "8px", "&:hover": { borderColor: "#b0b0b0" } }}>
        Back
      </Button>
    );
    const navyBtn = (label, onClick, extra = {}) => (
      <Button variant="contained" onClick={onClick}
        sx={{ bgcolor: NAVY, textTransform: "none", px: 5, py: 1, borderRadius: "8px", "&:hover": { bgcolor: NAVY_DARK }, ...extra }}>
        {label}
      </Button>
    );

    if (screen === 3) {
      return (
        <>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignText: "left" }}>
            <Typography>
              Payment Info
            </Typography>

          </Box>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: { sm: 3.5 } }}>
            <Box sx={{ display: "flex" }}>
              <Checkbox checked={form.paymentInfo} onChange={set("paymentInfo")}
                sx={{ p: 0.5, color: NAVY, "&.Mui-checked": { color: NAVY } }} />
              <Typography sx={{ fontSize: 14, fontWeight: 700, color: RED }}>
                *
              </Typography>
            </Box>
            <Box
              sx={{
                p: 1.5,
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                bgcolor: isAfterDeadline ? "#f8f9fa" : "#eef4fb",
                opacity: isAfterDeadline ? 0.55 : 1,
                filter: isAfterDeadline ? "grayscale(0.3)" : "none",
                transition: "all 0.2s ease",
                cursor: isAfterDeadline ? "not-allowed" : "default",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: ORANGE, letterSpacing: 0.6, mb: 1 }}>
                BEFORE AUG 7, 6:30 PM
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
                <Typography sx={{ fontSize: 14, color: "#333" }}>Munmazhalai through Nilai 7</Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>$220</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 14, color: "#333" }}>
                  Nilai 8 &nbsp;<Typography component="span" sx={{ fontSize: 12, color: "#888" }}>($250 + $30 Graduation Exp.)</Typography>
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>$280</Typography>
              </Box>
            </Box>

            {/* ── After deadline ── */}
            <Box
              sx={{
                p: 2,
                borderRadius: "10px",
                border: "1px solid #e5e7eb",
                bgcolor: isAfterDeadline ? "#eef4fb" : "#f8f9fa",
                opacity: isAfterDeadline ? 1 : 0.55,
                filter: isAfterDeadline ? "none" : "grayscale(0.3)",
                transition: "all 0.2s ease",
                cursor: isAfterDeadline ? "default" : "not-allowed",
              }}
            >
              <Typography sx={{ fontSize: 12, fontWeight: 700, color: RED, letterSpacing: 0.6, mb: 1 }}>
                AFTER AUG 7, 6:30 PM
              </Typography>
              <Box sx={{ display: "flex", justifyContent: "space-between", mb: 0.8 }}>
                <Typography sx={{ fontSize: 14, color: "#333" }}>
                  Munmazhalai through Nilai 7 &nbsp;<Typography component="span" sx={{ fontSize: 12, color: "#888" }}>($220 + $5 Shipping)</Typography>
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>$225</Typography>
              </Box>
              <Box sx={{ display: "flex", justifyContent: "space-between" }}>
                <Typography sx={{ fontSize: 14, color: "#333" }}>
                  Nilai 8 &nbsp;<Typography component="span" sx={{ fontSize: 12, color: "#888" }}>($250 + $30 Graduation + $5 Shipping)</Typography>
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: NAVY }}>$285</Typography>
              </Box>
            </Box>

          </Box>
          {isAfterDeadline && (
            <Box style={{ alignItems: "center", display: "flex" }}>
              <Typography sx={{ mt: 2, fontSize: 12, color: RED, fontWeight: 600 }}>
                ⚠️ The early registration deadline has passed. Shipping fee of $5 now applies.
              </Typography>
            </Box>
          )}
          <Box style={{ display: "flex", textAlign: "left", gap: 1, mt: 2.5 }}>
            <Typography sx={{ fontSize: 12 }}>
              Note: If you are registering more than one child, please make a single payment and use the same Zelle reference number for all registration forms.

              <br />
              <br />
              In the event of a cancellation, a refund will be issued after deducting the book cost of $50. Parents may collect the books according to the time slot noted in the registration form. Due to high volume of registration process, all refund payments will be processed and issued by the end of September only.
            </Typography>
          </Box>
          <Button fullWidth variant="contained" onClick={next} disabled={!form.paymentInfo} endIcon={<ArrowForwardIcon />}
            sx={{ bgcolor: NAVY, textTransform: "none", py: 1.4, borderRadius: "8px", fontSize: 15, fontWeight: 600, mt: 1, "&:hover": { bgcolor: NAVY_DARK } }}>
            Confirm &amp; Preview
          </Button>
        </>
      );
    }

    return (
      <Box sx={{ display: "flex", justifyContent: "center", gap: 2 }}>
        {cancelBtn}
        {screen === 2 && navyBtn("Submit", next)}
        {screen === 4 && navyBtn(loading ? "Submitting…" : "Next", submit, { minWidth: 130 })}
        {screen < 2 && navyBtn("Next", next)}
      </Box>
    );
  };

  // ═══════════════════════════════════════════════════════════════════════
  //  RENDER
  // ═══════════════════════════════════════════════════════════════════════
  return (
    <Box sx={{
      position: "relative", overflow: "hidden",
      minHeight: "100vh", pt: 14, pb: 8, px: 2,
      backgroundColor: "#FAF3FF",
      backgroundImage: `url(${registerBgImg})`,
      backgroundSize: "cover",
      backgroundPosition: "center top",
      backgroundAttachment: "fixed",
    }}>
      {/* Side kid illustration (desktop only) */}
      <Box component="img" src={registerSideImg} alt="" aria-hidden="true"
        sx={{
          position: "absolute", left: { lg: 0, xl: 40 }, top: "50%",
          transform: "translateY(-50%)",
          width: { lg: 300, xl: 340 }, display: { xs: "none", lg: "block" },
          pointerEvents: "none", zIndex: 0
        }} />

      <Box sx={{ position: "relative", zIndex: 1 }}>
        <Box sx={{ position: "relative", maxWidth: 760, mx: "auto", mb: 3, display: "flex", alignItems: "center", justifyContent: "center" }}>
          <IconButton onClick={() => navigate("/")}
            sx={{
              position: "absolute", left: 0,
              color: TITLE_BLUE,
              "&:hover": { bgcolor: "rgba(31,111,178,0.08)" }
            }}>
            <ArrowBackIcon />
          </IconButton>
          <Typography sx={{ fontSize: 26, fontWeight: 700, color: TITLE_BLUE }}>
            Cumming Tamil School&nbsp; {YEAR_TITLE}
          </Typography>
        </Box>

        <FancyStepper activeStep={SCREEN_TO_STEP[screen]} />

        <Paper sx={{
          maxWidth: 760, mx: "auto", mt: 3, borderRadius: "20px",
          p: { xs: 3, md: 4 }, boxShadow: "0 12px 40px rgba(80,60,120,0.12)"
        }}>
          <Box sx={{ minHeight: 320 }}>
            {screenRenderers[screen]()}
          </Box>

          <Divider sx={{ my: 3 }} />
          {renderFooter()}
        </Paper>
      </Box>

      <CreditWaiverDialog
        open={waiverOpen}
        onConfirm={() => { setForm(p => ({ ...p, creditWaiverAcknowledged: true })); setWaiverOpen(false); }}
        onClose={() => { setForm(p => ({ ...p, gradeApplyingFor: "" })); setWaiverOpen(false); }}
      />

      <BiliteracyWaiverDialog
        open={biliteracyWaiverOpen}
        onConfirm={() => { setForm(p => ({ ...p, creditWaiverAcknowledged: true })); setBiliteracyWaiverOpen(false); }}
        onClose={() => { setForm(p => ({ ...p, gradeApplyingFor: "" })); setBiliteracyWaiverOpen(false); }}
      />

      <BiliteracyWaiverDialog
        open={biliteracyCheckboxWaiverOpen}
        onConfirm={() => { setForm(p => ({ ...p, sealOfBiliteracy: true })); setBiliteracyCheckboxWaiverOpen(false); }}
        onClose={() => { setForm(p => ({ ...p, sealOfBiliteracy: false })); setBiliteracyCheckboxWaiverOpen(false); }}
      />

      <Dialog
        open={qrDialogOpen}
        onClose={() => setQrDialogOpen(false)}
        maxWidth="xs"
        fullWidth
        PaperProps={{ sx: { borderRadius: "16px", p: 1 } }}
      >
        <IconButton
          onClick={() => setQrDialogOpen(false)}
          sx={{ position: "absolute", top: 8, right: 8, color: "#9CA3AF" }}
        >
          <CloseIcon />
        </IconButton>
        <DialogTitle sx={{ textAlign: "center", fontWeight: 700, fontSize: "20px", color: "#3B4E8C", pt: 4, lineHeight: 1.4 }}>
          Are you a new student<br />or an existing student?
        </DialogTitle>
        <DialogContent>
          <Typography sx={{ textAlign: "center", color: "#6B7280", fontSize: "14px" }}>
            Please select an option to continue your registration.
          </Typography>
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center", gap: 2, pb: 3, px: 3 }}>
          <Button
            variant="contained"
            onClick={() => {
              setForm(p => ({ ...p, studentType: "new" }));
              setQrDialogOpen(false);
            }}
            sx={{
              backgroundColor: "#3B4E8C",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": { backgroundColor: "#2d3d6e" },
            }}
          >
            New Student
          </Button>

          <Button
            variant="outlined"
            onClick={() => {
              setForm(p => ({ ...p, studentType: "old" }));
              setQrDialogOpen(false);
            }}
            sx={{
              borderColor: "#3B4E8C",
              color: "#3B4E8C",
              borderRadius: "8px",
              textTransform: "none",
              fontWeight: 600,
              px: 3,
              "&:hover": { backgroundColor: "#f0f2f8" },
            }}
          >
            Existing Student
          </Button>
        </DialogActions>
      </Dialog>

      <Toast open={toast.open} severity={toast.severity} message={toast.message}
        handleClose={() => setToast(p => ({ ...p, open: false }))} />
    </Box>
  );
}
